/**
 * PointInTimeRecoveryDialog
 * --------------------------
 * Admin-only dialog that lets the operator pick a target Sydney datetime,
 * preview which backup will be used + how many transaction-log entries
 * will be replayed, then execute the recovery.
 *
 * Two-step UX:
 *   1. "Preview"  → GET /api/admin/pitr/preview?target_time=...
 *   2. "Execute"  → POST /api/admin/pitr/execute (with confirm: true)
 *
 * After execute, the polling loop in the parent restore-status component
 * picks up the new restore_id automatically because PITR re-uses the
 * existing restore_sessions schema.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertTriangle, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Format a Date object as a value compatible with <input type="datetime-local">
// Always Sydney clock — the backend interprets naive ISO as Sydney TZ.
function formatLocalInput(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export const PointInTimeRecoveryDialog = ({ open, onOpenChange, onComplete }) => {
  const [targetTime, setTargetTime] = useState(formatLocalInput(new Date()));
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const reset = () => {
    setPreview(null);
    setConfirmText('');
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const runPreview = async () => {
    if (!targetTime) {
      toast.error('Pick a target date & time first');
      return;
    }
    setPreviewing(true);
    try {
      // The backend interprets naive ISO as Sydney TZ — no need to send :00.
      const url = `${API}/admin/pitr/preview?target_time=${encodeURIComponent(targetTime)}`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      if (!data.ok) {
        toast.error(data.reason || 'No backup available before that time');
        setPreview(null);
        return;
      }
      setPreview(data);
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
      // eslint-disable-next-line no-console
      console.error('PITR preview failed', e);
    } finally {
      setPreviewing(false);
    }
  };

  const runExecute = async () => {
    if (!preview) {
      toast.error('Run Preview first');
      return;
    }
    if (confirmText.trim().toUpperCase() !== 'RESTORE') {
      toast.error('Type RESTORE to confirm');
      return;
    }
    setExecuting(true);
    try {
      const res = await fetch(`${API}/admin/pitr/execute`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_time: targetTime, confirm: true }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      toast.success(
        `PITR queued — using ${data.backup?.filename}. Watch the Restore status panel for progress.`,
      );
      if (onComplete) onComplete(data.restore_id);
      close();
    } catch (e) {
      toast.error(`PITR failed to start: ${e.message}`);
      // eslint-disable-next-line no-console
      console.error('PITR execute failed', e);
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent
        className="max-w-2xl"
        data-testid="pitr-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            Point-in-Time Recovery
          </DialogTitle>
          <DialogDescription>
            Pick a target moment in Sydney time. We'll restore the closest backup before that time, then replay every transaction-log entry up to your target.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="pitr-target">Target time (Australia/Sydney)</Label>
            <div className="flex gap-2">
              <Input
                id="pitr-target"
                type="datetime-local"
                value={targetTime}
                onChange={(e) => {
                  setTargetTime(e.target.value);
                  setPreview(null);
                }}
                data-testid="pitr-target-input"
              />
              <Button
                onClick={runPreview}
                disabled={previewing}
                data-testid="pitr-preview-btn"
                variant="secondary"
              >
                {previewing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Preview
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Naive datetime is interpreted as Sydney local time.
            </p>
          </div>

          {preview && (
            <div
              className="border rounded-lg p-4 bg-muted/40 space-y-2"
              data-testid="pitr-preview-panel"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">Selected backup</span>
                <span className="font-mono text-sm">{preview.backup.filename}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Backup taken at</span>
                <span className="font-mono">{preview.backup.backup_time}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Backup size</span>
                <span>{preview.backup.size_mb} MB</span>
              </div>
              <hr className="border-muted" />
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Replay window</span>
                <span className="font-mono text-xs">
                  ({preview.replay_window.from_exclusive}, {preview.replay_window.to_inclusive}]
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">Transactions to replay</span>
                <span className="text-lg font-mono" data-testid="pitr-tx-count">
                  {preview.transactions_to_replay}
                </span>
              </div>
              {preview.transactions_to_replay > 0 && (
                <div className="text-xs text-muted-foreground">
                  By op:{' '}
                  {Object.entries(preview.by_operation)
                    .filter(([, n]) => n > 0)
                    .map(([k, n]) => `${k}: ${n}`)
                    .join('  ·  ') || '—'}
                </div>
              )}
            </div>
          )}

          {preview && (
            <div className="border border-destructive/50 rounded-lg p-3 bg-destructive/5">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-destructive shrink-0" />
                <div className="text-sm space-y-2 flex-1">
                  <div className="font-semibold text-destructive">
                    This will overwrite the current database.
                  </div>
                  <div className="text-muted-foreground">
                    Type{' '}
                    <span className="font-mono font-bold text-destructive">RESTORE</span>{' '}
                    to confirm.
                  </div>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="RESTORE"
                    className="font-mono"
                    data-testid="pitr-confirm-input"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={executing}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={runExecute}
            disabled={
              !preview ||
              executing ||
              confirmText.trim().toUpperCase() !== 'RESTORE'
            }
            data-testid="pitr-execute-btn"
          >
            {executing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Run Point-in-Time Recovery
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PointInTimeRecoveryDialog;
